import { AuditLogItems } from "@/modules/enums";
import { createLogItem } from "@/modules/helpers";
import { AuditLogApi, UsersApi } from "@/services";
import { CytologyEntities } from "@/services/cytology";

export const grids = {
  namespaced: true,
  state: () => {
    const defaultGrid = {
      allowedPageSizes: [],
      columns: [],
      filterPanel: { filterEnabled: true },
      filterValue: null,
      pageIndex: 0,
      pageSize: 10,
      searchText: "",
      selectedRowKeys: []
    };
    return {
      openCases: defaultGrid,
      allCases: defaultGrid,
      caseDistributions: defaultGrid,
      caseHistory: defaultGrid,
      orders: defaultGrid,
      tasks: defaultGrid,
      slides: defaultGrid,
      tags: defaultGrid,
      prefixes: defaultGrid,
      distributions: defaultGrid,
      insurance: defaultGrid,
      orderMaintenance: defaultGrid,
      roles: defaultGrid,
      activity: defaultGrid,
      users: defaultGrid,
      contact: defaultGrid,
      labSettings: defaultGrid,
      typeCodes: defaultGrid,
      [CytologyEntities.CytDiagnosticCodes]: defaultGrid,
      [CytologyEntities.CytDiagnosticMacros]: defaultGrid,
      [CytologyEntities.CytQuestions]: defaultGrid,
      [CytologyEntities.CytMolecResults]: defaultGrid,
      [CytologyEntities.CytMolecTests]: defaultGrid,
      [CytologyEntities.CytMolecPanels]: defaultGrid,
      [CytologyEntities.CytTriggers]: defaultGrid,
      lastCallTimes: {}
    };
  },
  mutations: {
    setGridState(state, { gridState, columns, name, skipSaving }) {
      for (let i = 0; i < Math.min(gridState.columns.length, columns.length); i++) {
        const gridColumn = gridState.columns[i];
        const column = columns.find(col => col.dataField === gridColumn.dataField);
        if (column) {
          gridColumn.filterValue = column.filterValue;
          gridColumn.filterValues = column.filterValues;
        }
        if (column.defaultFilterValues) {
          gridColumn.filterValues = column.defaultFilterValues;
        }
        if (column.defaultFilterValue) {
          gridColumn.filterValue = column.defaultFilterValue;
        }
      }
      const newGrid = { ...gridState, searchText: "", selectedRowKeys: [], pageIndex: 0 };
      const oldGrid = { ...state[name] };
      state[name] = newGrid;
      // Check whether relevant properties have changed before saving grid to back end
      function shouldSaveGrid() {
        if (skipSaving) {
          return false;
        }
        function mapGrid(grid) {
          let mappedGrid = {};
          const mappedColumns = grid.columns.map(e => {
            return {
              name: e?.name,
              sortIndex: e?.sortIndex,
              sortOrder: e?.sortOrder,
              visible: e?.visible,
              visibleIndex: e?.visibleIndex,
              width: e?.width
            };
          });
          mappedGrid.columns = mappedColumns;
          mappedGrid.pageSize = grid.pageSize;
          return mappedGrid;
        }
        const newGridMap = mapGrid(newGrid);
        const oldGridMap = mapGrid(oldGrid);
        if (newGridMap.pageSize !== oldGrid.pageSize) {
          return true;
        }
        if (newGrid.columns.length !== oldGrid.columns.length) {
          return true;
        }
        if (
          newGrid.columns.filter(e => e.visible).length !==
          oldGrid.columns.filter(e => e.visible).length
        ) {
          return true;
        }
        for (let i = 0; i < newGrid.columns.length; i++) {
          const newColumn = newGridMap.columns[i];
          const oldColumn = oldGridMap.columns[i];
          for (const key of Object.keys(newColumn)) {
            if (newColumn[key] !== oldColumn[key]) {
              return true;
            }
          }
        }
        return false;
      }
      if (shouldSaveGrid()) {
        delete newGrid?.filterValue;
        delete newGrid?.searchText;
        delete newGrid?.pageIndex;
        delete newGrid?.selectedRowKeys;
        const payload = { gridName: name, gridState: JSON.stringify(newGrid) };
        const callTime = new Date().getTime();
        state.lastCallTimes[name] = callTime;
        setTimeout(() => {
          if (callTime === state.lastCallTimes[name]) {
            UsersApi.saveGridState(payload).then(() => {
              const logItem = createLogItem({}, AuditLogItems.Other);
              logItem.comments = `Changed grid ${name}\n\n${JSON.stringify(oldGrid, null, 2)}`;
              AuditLogApi.insertLogMessage(logItem);
            });
          }
        }, 1000);
      }
    }
  },
  actions: {
    async setGridState({ commit }, payload) {
      commit("setGridState", payload);
    },
    clearAllGrids({ dispatch, state }) {
      for (const gridName of Object.keys(state)) {
        dispatch("resetGrid", { gridName, skipSaving: true });
      }
    },
    resetGrid({ commit }, { gridName, skipSaving }) {
      const defaultGrid = {
        allowedPageSizes: [],
        columns: [],
        filterPanel: { filterEnabled: true },
        filterValue: null,
        pageIndex: 0,
        pageSize: 10,
        searchText: "",
        selectedRowKeys: []
      };
      commit("setGridState", {
        gridState: defaultGrid,
        columns: [],
        name: gridName,
        skipSaving
      });
    }
  }
};
